import React from 'react';
import { Input } from './ui/input';

export const PACE_PRESETS = {
  fast: { label: 'Fast', min: 2, max: 10 },
  moderate: { label: 'Moderate', min: 10, max: 25 },
  slow: { label: 'Slow', min: 30, max: 60 },
};

export const paceGaps = (pace) => {
  if (pace.preset === 'custom') {
    const min = Math.max(1, parseInt(pace.min_gap_min, 10) || 2);
    return { min, max: Math.max(min, parseInt(pace.max_gap_min, 10) || 10) };
  }
  const p = PACE_PRESETS[pace.preset] || PACE_PRESETS.fast;
  return { min: p.min, max: p.max };
};

export const pacePerDay = (pace) => {
  const { min, max } = paceGaps(pace);
  let perDay = Math.max(1, Math.floor(480 / ((min + max) / 2)));
  const limit = parseInt(pace.daily_limit, 10);
  if (limit > 0) perDay = Math.min(perDay, limit);
  return perDay;
};

export function PaceEditor({ pace, onChange }) {
  const set = (patch) => onChange({ ...pace, ...patch });
  const { min, max } = paceGaps(pace);
  const limit = parseInt(pace.daily_limit, 10) || 0;
  const btnCls = (selected) => `text-[11px] border rounded-sm px-2.5 py-1.5 transition-colors ${selected
    ? 'border-sky-500/60 bg-sky-600/15 text-sky-200'
    : 'border-border bg-secondary/40 text-muted-foreground hover:text-foreground'}`;
  return (
    <div className="space-y-2" data-testid="pace-editor">
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(PACE_PRESETS).map(([key, p]) => (
          <button key={key} type="button" onClick={() => set({ preset: key })} data-testid={`pace-preset-${key}`}
            className={btnCls(pace.preset === key)}>
            {p.label} <span className="opacity-60 font-mono">{p.min}–{p.max} min</span>
          </button>
        ))}
        <button type="button" onClick={() => set({ preset: 'custom' })} data-testid="pace-preset-custom"
          className={btnCls(pace.preset === 'custom')}>
          Custom
        </button>
      </div>
      {pace.preset === 'custom' && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="pace-custom-row">
          Gap between emails:
          <Input type="number" min="1" max="180" value={pace.min_gap_min} data-testid="pace-min-input"
            onChange={e => set({ min_gap_min: e.target.value })}
            className="w-16 h-7 bg-secondary border-border text-foreground rounded-sm text-xs px-1.5" />
          to
          <Input type="number" min="1" max="240" value={pace.max_gap_min} data-testid="pace-max-input"
            onChange={e => set({ max_gap_min: e.target.value })}
            className="w-16 h-7 bg-secondary border-border text-foreground rounded-sm text-xs px-1.5" />
          minutes
        </div>
      )}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        Daily limit:
        <Input type="number" min="0" max="500" value={pace.daily_limit} placeholder="∞" data-testid="pace-daily-limit"
          onChange={e => set({ daily_limit: e.target.value })}
          className="w-20 h-7 bg-secondary border-border text-foreground rounded-sm text-xs px-1.5" />
        emails/day (empty = no limit)
      </div>
      <p className="text-[10px] text-sky-300" data-testid="pace-summary">
        ≈ {pacePerDay(pace)} emails per business day · {min}–{max} min apart{limit > 0 ? ` · hard cap ${limit}/day` : ''}
      </p>
    </div>
  );
}
